/**
 * Permission matching (pure).
 *
 *  - exact code:          `iam.user.read`
 *  - resource wildcard:   `core.customer.*`  → any action on the resource
 *  - module wildcard:     `core.*`           → any permission of the module
 *  - super permission:    a user holding `*` passes every check
 */
export type PermissionRequirement = string | readonly string[];

export function matchesPermission(granted: ReadonlySet<string>, code: string): boolean {
  if (granted.has('*') || granted.has(code)) return true;
  if (code.endsWith('.*')) {
    const prefix = code.slice(0, -1); // keep the trailing dot
    for (const g of granted) if (g.startsWith(prefix)) return true;
    return false;
  }
  // granted wildcard, e.g. user holds `core.customer.*`
  const parts = code.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    if (granted.has(`${parts.slice(0, i).join('.')}.*`)) return true;
  }
  return false;
}

/** `mode = 'any'` (default): at least one; `'all'`: every requirement. Empty/undefined → allowed. */
export function hasPermission(
  granted: ReadonlySet<string>,
  required: PermissionRequirement | undefined | null,
  mode: 'any' | 'all' = 'any',
): boolean {
  if (!required || (Array.isArray(required) && required.length === 0)) return true;
  const list: readonly string[] = typeof required === 'string' ? [required] : required;
  return mode === 'all' ? list.every((c) => matchesPermission(granted, c)) : list.some((c) => matchesPermission(granted, c));
}
